import type { AbcKlasse, PdpBlockZahlen, PdpDetails, PdpGap, PdpUsp } from '@awf/shared';
import { type ReactNode, useMemo } from 'react';
import { klasse } from '../ui/klasse.js';
import { alsHtml } from './als-html.js';
import stile from './befund-bloecke.module.css';
import { type BlockSchluessel, PDP_BLOECKE } from './pdp-bloecke.js';
import { type RankingAnzeige, type RankingTabelle, rankingAnzeige } from './ranking.js';
import website from './website-text.module.css';

/** Bedeutung laut Martins Phase 6 (Note `Note ABC` in der Analyse). */
const KLASSE_TEXT: Record<AbcKlasse, string> = {
  A: 'eigener Abschnitt im Text',
  B: 'interner Link auf eine bestehende ERGO-Seite',
  C: 'verworfen, gehört nicht hierher',
};

const USP_STATUS: Record<string, string> = {
  nur_fliesstext: 'nur im Fließtext',
  nicht_auffindbar: 'nicht auffindbar',
  im_vorteile_block: 'im Vorteile-Block',
  im_leistungen_block: 'im Leistungen-Block',
};

/** Einleitungszeilen aus Martins Formular (Block 2 und 6). */
const BLOCK_INTRO: Partial<Record<BlockSchluessel, string>> = {
  ueberschriften: 'H2/H3-Vorschläge für die eigene Seite, keine A/B/C-Klassifikation nötig.',
  faq: 'Häufig gesuchte Nutzerfragen, die ERGO beantworten kann und die sich als FAQ-Eintrag eignen.',
};

const GRAD: Record<string, string> = {
  exklusiv: 'Exklusive Leistung',
  besser: 'Leistungsvorsprung',
};

/**
 * Gruende, aus denen der USP-Agent eine PAA-Frage nicht als FAQ-Chance aufnimmt (Feld `grund`
 * in `usp_paa_verworfen`), im Wortlaut des n8n-Formulars. Unbekannte Kategorien bleiben roh
 * sichtbar, statt zu verschwinden.
 */
const FAQ_GRUND: Record<string, string> = {
  ranking_vergleich: 'Ranking-/Vergleichsfrage',
  wettbewerberfrage: 'Frage zu einem Wettbewerber',
  cross_selling: 'Anderes Versicherungsprodukt',
  kein_versicherungsbezug: 'Kein Versicherungsbezug',
  themenfremd: 'Themenfremd',
  kein_produktbezug: 'Kein Produktbezug',
  bereits_in_faq: 'Bereits in der FAQ beantwortet',
  keine_ergo_antwort: 'Von ERGO nicht beantwortbar',
  ueber_limit: 'Nicht unter den Top 6',
};

export function AbcBadge({ klasse: k }: { klasse: AbcKlasse }) {
  return (
    <span
      role="img"
      aria-label={`Klasse ${k}: ${KLASSE_TEXT[k]}`}
      title={KLASSE_TEXT[k]}
      className={klasse(stile.badge, stile[`badge${k}`])}
    >
      {k}
    </span>
  );
}

export function AbcLegende() {
  return (
    <dl className={stile.legende} aria-label="Legende der Bewertung">
      {(['A', 'B', 'C'] as const).map((k) => (
        <div key={k} className={stile.legendeEintrag}>
          <dt>
            <AbcBadge klasse={k} />
          </dt>
          <dd>{KLASSE_TEXT[k]}</dd>
        </div>
      ))}
    </dl>
  );
}

export function BlockKacheln({
  zahlen,
  onWaehle,
}: {
  zahlen: PdpBlockZahlen | undefined;
  onWaehle: (s: BlockSchluessel) => void;
}) {
  return (
    <ul className={stile.kacheln} aria-label="Empfehlungen je Block">
      {PDP_BLOECKE.map((b) => {
        const s = b.schluessel;
        return (
          <li key={s}>
            <button
              type="button"
              className={stile.kachel}
              onClick={() => onWaehle(s)}
              // Ohne Empfehlung gibt es keinen Abschnitt, zu dem die Kachel springen koennte.
              disabled={zahlen?.[s] === 0}
            >
              <span className={stile.kachelZahl}>{zahlen ? zahlen[s] : '–'}</span>
              <span className={stile.kachelTitel}>{b.kurz}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Ein Block wie eine Sektion im n8n-Formular ("Block 1: Keyword-Abdeckung"): Ueberschrift,
 * Einleitung, Eintraege, immer offen. Frueher ein Aufklapper; im Formular stehen alle Bloecke
 * untereinander, und genau so liest die Redaktion sie (Rueckmeldung 2026-10-13).
 */
function Abschnitt({
  id,
  titel,
  intro,
  children,
}: {
  id: string;
  titel: string;
  intro?: string | undefined;
  children: ReactNode;
}) {
  return (
    <section id={id} className={stile.abschnitt} aria-labelledby={`${id}-titel`}>
      <h2 id={`${id}-titel`} className={stile.abschnittTitel}>
        {titel}
      </h2>
      {intro && <p className={stile.intro}>{intro}</p>}
      {children}
    </section>
  );
}

function GapListe({ gaps }: { gaps: PdpGap[] }) {
  if (gaps.length === 0) return <p className={stile.leise}>Keine Empfehlungen in diesem Block.</p>;
  return (
    <ul className={stile.eintraege}>
      {gaps.map((g, i) => (
        <li
          key={`${g.titel}-${i}`}
          className={klasse(stile.eintrag, g.klasse === 'C' && stile.abgeschwaecht)}
        >
          <div className={stile.eintragKopf}>
            <strong>{g.titel}</strong>
            {g.status && <span className={stile.status}>({g.status})</span>}
            {g.klasse && <AbcBadge klasse={g.klasse} />}
          </div>
          {g.begruendung && <p className={stile.begruendung}>{g.begruendung}</p>}
          {g.linkziel && (
            <p className={stile.linkziel}>
              Linkziel:{' '}
              <a href={g.linkziel.url} target="_blank" rel="noreferrer">
                {g.linkziel.url}
              </a>
              {g.linkziel.h1 && (
                <>
                  {' '}
                  <em>{g.linkziel.h1}</em>
                </>
              )}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function UspEintrag({ usp, modus }: { usp: PdpUsp; modus: 'offen' | 'platziert' | 'schwach' }) {
  const zeilen: { titel: string; text: string }[] = [];
  if (usp.differenzierungsgrad === 'exklusiv' && usp.fehltBei.length > 0) {
    zeilen.push({ titel: 'Fehlt bei', text: usp.fehltBei.join(', ') });
  } else if (usp.differenzierungsgrad === 'besser' && usp.auchBei.length > 0) {
    zeilen.push({
      titel: 'Auch bei',
      text: usp.auchBei.join(', ') + (usp.vergleich ? ` – ${usp.vergleich}` : ''),
    });
  }
  if (usp.beleg) zeilen.push({ titel: 'Beleg im Text', text: `„${usp.beleg}“` });
  if (usp.korrigiertVon !== undefined) {
    const von = USP_STATUS[usp.korrigiertVon] ?? usp.korrigiertVon;
    const auf = USP_STATUS[usp.status] ?? usp.status;
    zeilen.push({
      titel: 'Hinweis',
      text: `automatisch von „${von}“ auf „${auf}“ korrigiert, da kein passendes Zitat im Seitentext gefunden wurde.`,
    });
  }
  const f = usp.fundstelle;
  if (f) {
    const teile: string[] = [];
    if (f.typ === 'pdf' && f.pdfDokument) teile.push(`PDF „${f.pdfDokument}“`);
    else if (f.typ === 'text') teile.push('Seitentext');
    if (f.abschnitt) teile.push(`Abschnitt „${f.abschnitt}“`);
    if (f.lage) teile.push(f.lage);
    if (f.zitat) teile.push(`Zitat: „${f.zitat}“`);
    if (teile.length > 0)
      zeilen.push({ titel: 'Fundstelle (Quelle des Merkmals)', text: teile.join(' · ') });
  }
  if (modus === 'offen' && usp.empfehlung)
    zeilen.push({ titel: 'Empfehlung', text: usp.empfehlung });
  const grad = usp.differenzierungsgrad ? GRAD[usp.differenzierungsgrad] : undefined;
  return (
    <li
      className={klasse(
        stile.eintrag,
        modus === 'platziert' && stile.platziert,
        modus === 'schwach' && stile.abgeschwaecht,
      )}
    >
      <div className={stile.eintragKopf}>
        {modus === 'platziert' && (
          <span className={stile.symbolPlatziert} role="img" aria-label="bereits platziert">
            ✓
          </span>
        )}
        {modus === 'schwach' && (
          <span className={stile.symbolSchwach} role="img" aria-label="zu schwach">
            –
          </span>
        )}
        <strong>{usp.merkmal}</strong>
        {usp.status && (
          <span className={stile.status}>({USP_STATUS[usp.status] ?? usp.status})</span>
        )}
      </div>
      {grad && (
        <p className={stile.gradZeile}>
          <span className={klasse(stile.grad, stile[`grad_${usp.differenzierungsgrad}`])}>
            {grad}
          </span>
        </p>
      )}
      {zeilen.length > 0 && (
        <ul className={stile.uspZeilen}>
          {zeilen.map((z) => (
            <li key={z.titel}>
              <strong>{z.titel}:</strong> {z.text}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function uspLage(d: PdpDetails): string | undefined {
  const { offen, platziert, schwach } = d.usps;
  const alle = offen.length + platziert.length + schwach.length;
  if (alle === 0)
    return 'Es wurden keine differenzierenden USP-Merkmale gegenüber den Wettbewerbern identifiziert.';
  if (offen.length > 0) return undefined;
  if (platziert.length > 0 && schwach.length === 0) {
    return `Alle ${alle} erkannten USP-Merkmale sind bereits gut und prominent platziert.`;
  }
  if (platziert.length === 0) {
    return `Es wurden ${alle} USP-Merkmale erkannt, keines davon ist stark genug differenzierend für eine hervorgehobene Empfehlung.`;
  }
  return `Alle ${alle} erkannten USP-Merkmale sind bereits platziert oder zu schwach differenzierend für eine Empfehlung.`;
}

function UspBlock({ details }: { details: PdpDetails }) {
  const { offen, platziert, schwach } = details.usps;
  const lage = uspLage(details);
  const abgedeckt = platziert.length + schwach.length;
  return (
    <>
      {details.wettbewerber.length > 0 && (
        <>
          <p className={stile.unterTitel}>Verglichene Wettbewerber</p>
          <ul className={stile.wettbewerber}>
            {details.wettbewerber.map((w, i) => (
              <li key={`${w.domain}-${i}`}>
                {w.domain}
                {w.titel && ` – „${w.titel}“`}
                {w.url && (
                  <>
                    {' ('}
                    <a href={w.url} target="_blank" rel="noreferrer">
                      Link
                    </a>
                    {')'}
                  </>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {lage && <p className={stile.lage}>{lage}</p>}
      {offen.length > 0 && (
        <ul className={stile.eintraege}>
          {offen.map((u, i) => (
            <UspEintrag key={`${u.merkmal}-${i}`} usp={u} modus="offen" />
          ))}
        </ul>
      )}
      {abgedeckt > 0 && (
        // Immer offen, nicht zuklappbar (Rueckmeldung 2026-10-13): im Formular ein <details>.
        <div className={stile.unterAbschnitt}>
          <h3 className={stile.unterTitel}>
            Bereits abgedeckte oder zu schwache USP-Merkmale ({abgedeckt})
          </h3>
          <ul className={stile.eintraege}>
            {platziert.map((u, i) => (
              <UspEintrag key={`p-${u.merkmal}-${i}`} usp={u} modus="platziert" />
            ))}
            {schwach.map((u, i) => (
              <UspEintrag key={`s-${u.merkmal}-${i}`} usp={u} modus="schwach" />
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function FaqBlock({ details }: { details: PdpDetails }) {
  const verworfen = details.faqVerworfen ?? [];
  const aufklapperTitel = `Geprüfte, aber nicht aufgenommene Fragen anzeigen (${verworfen.length})`;
  return (
    <>
      {details.faq.length === 0 ? (
        <p className={stile.lage}>
          Es wurden keine geeigneten FAQ-Chancen gefunden (kein People-Also-Ask, das ERGO
          beantworten kann und das nicht schon in einer bestehenden FAQ-Sektion der Seite steht).
        </p>
      ) : (
        <ul className={stile.eintraege}>
          {details.faq.map((f, i) => (
            <li key={`${f.frage}-${i}`} className={stile.eintrag}>
              <strong>{f.frage}</strong>
              {f.signal && <p className={stile.begruendung}>{f.signal}</p>}
            </li>
          ))}
        </ul>
      )}
      {verworfen.length > 0 && (
        // Bewusste Ausnahme von "nur der Originaltext ist aufklappbar" (Rueckmeldung 2026-10-13):
        // Die Liste ist Nachweis, keine Empfehlung, und wuerde offen den Block ueberlagern.
        // Optik wie der Originaltext auf Wunsch der Redaktion (2026-10-23).
        <details className={stile.originalAufklapper}>
          <summary>{aufklapperTitel}</summary>
          <ul className={stile.eintraege}>
            {verworfen.map((f, i) => (
              <li key={`${f.frage}-${i}`} className={stile.eintrag}>
                <div className={stile.eintragKopf}>
                  <strong>{f.frage}</strong>
                  {f.grund && (
                    <span className={stile.status}>({FAQ_GRUND[f.grund] ?? f.grund})</span>
                  )}
                </div>
                {f.begruendung && <p className={stile.begruendung}>{f.begruendung}</p>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

function Tabelle({ t, beschriftung }: { t: RankingTabelle; beschriftung: string }) {
  return (
    <div className={stile.tabelleRahmen}>
      <table className={stile.tabelle} aria-label={beschriftung}>
        <thead>
          <tr>
            {t.kopf.map((k, i) => (
              <th key={k} scope="col" className={t.rechts.includes(i) ? stile.rechts : undefined}>
                {k}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {t.zeilen.map((z, j) => (
            <tr key={j}>
              {z.map((c, i) => (
                <td key={i} className={t.rechts.includes(i) ? stile.rechts : undefined}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RankingInhalt({ a }: { a: RankingAnzeige }) {
  const k = a.kannibalisierung;
  return (
    <>
      {a.status && (
        <p className={klasse(stile.ampel, stile[`ampel_${a.status.ton}`])}>{a.status.text}</p>
      )}
      {a.analyseText && <p className={stile.begruendung}>{a.analyseText}</p>}
      {a.top3 && (
        <>
          <p className={stile.unterTitel}>Top 3 Rankings dieser Seite</p>
          <Tabelle t={a.top3} beschriftung="Top 3 Rankings dieser Seite" />
        </>
      )}
      {a.nonbrand && (
        <>
          <p className={stile.unterTitel}>
            Non-Brand-Keywords dieser Seite (Search Console, letzte 90 Tage)
          </p>
          <Tabelle t={a.nonbrand} beschriftung="Non-Brand-Keywords dieser Seite" />
          {a.gscHinweis && <p className={stile.warnung}>{a.gscHinweis}</p>}
        </>
      )}
      {a.identitaet && (
        <>
          <p className={stile.unterTitel}>{a.identitaet.ueberschrift}</p>
          <Tabelle
            t={a.identitaet.tabelle}
            beschriftung="Sichtbarkeit unter eigenen Bezeichnungen"
          />
        </>
      )}
      {a.fehlRanking && (
        <p className={stile.warnung}>
          <strong>Rankt fälschlicherweise für:</strong> {a.fehlRanking.produkt}
          {a.fehlRanking.url && (
            <>
              {' ('}
              <a href={a.fehlRanking.url} target="_blank" rel="noreferrer">
                Link
              </a>
              {')'}
            </>
          )}
        </p>
      )}
      {k && (
        <>
          <p className={stile.unterTitel}>
            {k.titel}
            {k.risiko && <span className={stile.risiko}>Kannibalisierungsrisiko</span>}
          </p>
          <div className={stile.tabelleRahmen}>
            <table className={stile.tabelle} aria-label="Kannibalisierungs-Check">
              <thead>
                <tr>
                  <th scope="col" className={stile.rechts}>
                    Position
                  </th>
                  <th scope="col">Herkunft</th>
                  <th scope="col">Typ</th>
                  <th scope="col">Seite</th>
                </tr>
              </thead>
              <tbody>
                {k.zeilen.map((z, i) => (
                  <tr key={i} className={z.istZiel ? stile.ziel : undefined}>
                    <td className={stile.rechts}>{z.position}</td>
                    <td>{z.herkunft}</td>
                    <td>
                      {z.typ} {z.fremdeDomain && <span className="eds-chip">{z.fremdeDomain}</span>}
                    </td>
                    <td>
                      {z.seite.url ? (
                        <a href={z.seite.url} target="_blank" rel="noreferrer">
                          {z.seite.text}
                        </a>
                      ) : (
                        z.seite.text
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function BlockInhalt({ s, details }: { s: BlockSchluessel; details: PdpDetails }) {
  switch (s) {
    case 'keywords':
      return <GapListe gaps={details.keywords} />;
    case 'entities':
      return <GapListe gaps={details.entities} />;
    case 'inhalte':
      return <GapListe gaps={details.inhalte} />;
    case 'ueberschriften':
      return details.ueberschriften.length === 0 ? (
        <p className={stile.leise}>Keine Empfehlungen in diesem Block.</p>
      ) : (
        <ul className={stile.eintraege}>
          {details.ueberschriften.map((u, i) => (
            <li key={`${u.thema}-${i}`} className={stile.eintrag}>
              <strong>{u.thema}</strong>
              {u.keywordVorschlag && (
                <>
                  {' '}
                  – Vorschlag: <code>{u.keywordVorschlag}</code>
                </>
              )}
            </li>
          ))}
        </ul>
      );
    case 'usps':
      return <UspBlock details={details} />;
    case 'faq':
      return <FaqBlock details={details} />;
  }
}

function blockZahl(s: BlockSchluessel, d: PdpDetails): number {
  if (s === 'usps') return d.usps.offen.length + d.usps.platziert.length + d.usps.schwach.length;
  return d[s].length;
}

/**
 * Der Befund in der Reihenfolge des n8n-Review-Formulars (Workflow eJQwYUTdOvn5Rbnj, Code
 * "Formular"): Originaltext zum Aufklappen, Ranking-Analyse, dann Block 1 bis 6. Wie dort
 * entfallen die Bloecke 1 bis 4 ohne Eintrag; USPs und FAQs stehen immer da, weil auch ihr
 * Leerfall eine Aussage ist.
 */
export function BefundBericht({ details }: { details: PdpDetails }) {
  // alsHtml bereinigt per Allowlist (DOMPurify) -- dieselbe Funktion wie auf der Freigabeseite.
  const html = useMemo(() => ({ __html: alsHtml(details.originaltext) }), [details.originaltext]);
  const ranking = details.ranking ? rankingAnzeige(details.ranking) : undefined;
  const sichtbar = PDP_BLOECKE.filter(
    (b) =>
      b.schluessel === 'usps' || b.schluessel === 'faq' || blockZahl(b.schluessel, details) > 0,
  );
  return (
    <div className={stile.bericht}>
      <details id="block-originaltext" className={stile.originalAufklapper}>
        <summary>Originaltext anzeigen</summary>
        {details.originaltext ? (
          <div
            className={`${website.websiteText} ${stile.originaltext}`}
            dangerouslySetInnerHTML={html}
          />
        ) : (
          <p className={stile.leise}>Kein Originaltext hinterlegt.</p>
        )}
      </details>
      {ranking && (
        <Abschnitt id="block-ranking" titel="Ranking-Analyse">
          <RankingInhalt a={ranking} />
        </Abschnitt>
      )}
      {details.empfehlungenUnlesbar ? (
        <div className="eds-alert eds-alert--warning" role="status">
          <p>
            Die Empfehlungen dieser Analyse sind nicht lesbar. Originaltext und Ranking stehen
            trotzdem zur Verfügung.
          </p>
        </div>
      ) : (
        sichtbar.map((b) => (
          <Abschnitt
            key={b.schluessel}
            id={`block-${b.schluessel}`}
            titel={`Block ${b.nummer}: ${b.titel}`}
            intro={BLOCK_INTRO[b.schluessel]}
          >
            <BlockInhalt s={b.schluessel} details={details} />
          </Abschnitt>
        ))
      )}
    </div>
  );
}
